import React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Heading2, Quote } from 'lucide-react';

interface RichTextProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const toolbarBtn = (active: boolean): React.CSSProperties => ({
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  border: 0,
  background: active ? 'var(--hj-beige-deep)' : 'transparent',
  color: 'var(--hj-beige-ink)',
  cursor: 'pointer',
  fontFamily: 'inherit',
});

const Toolbar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  if (!editor) return null;

  const promptLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      className="flex items-center gap-1"
      style={{
        padding: '6px 8px',
        borderBottom: '1px solid var(--hj-beige-line)',
        background: 'var(--hj-beige-hover)',
        borderTopLeftRadius: 'var(--hj-r-sm)',
        borderTopRightRadius: 'var(--hj-r-sm)',
      }}
    >
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} style={toolbarBtn(editor.isActive('bold'))} aria-label="Bold" title="Bold (⌘B)">
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} style={toolbarBtn(editor.isActive('italic'))} aria-label="Italic" title="Italic (⌘I)">
        <Italic className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} style={toolbarBtn(editor.isActive('heading', { level: 2 }))} aria-label="Heading" title="Heading">
        <Heading2 className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} style={toolbarBtn(editor.isActive('bulletList'))} aria-label="Bullet list" title="Bullet list">
        <List className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} style={toolbarBtn(editor.isActive('orderedList'))} aria-label="Numbered list" title="Numbered list">
        <ListOrdered className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} style={toolbarBtn(editor.isActive('blockquote'))} aria-label="Quote" title="Quote">
        <Quote className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={promptLink} style={toolbarBtn(editor.isActive('link'))} aria-label="Link" title="Link">
        <LinkIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const RichText: React.FC<RichTextProps> = ({ value, onChange, placeholder, minHeight = 120 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'rt-content',
        style: `min-height:${minHeight}px;padding:10px 12px;outline:none;color:var(--hj-beige-ink);font-size:14px;line-height:1.55;`,
      },
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    const current = editor.getHTML();
    // Editor renders '' as '<p></p>'; treat those as equivalent so we don't reset cursor on every keystroke.
    const norm = (h: string) => (h === '<p></p>' ? '' : h);
    if (norm(incoming) !== norm(current)) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  return (
    <div
      style={{
        borderRadius: 'var(--hj-r-sm)',
        border: '1px solid var(--hj-beige-line)',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <Toolbar editor={editor} />
      <div style={{ position: 'relative' }}>
        {editor && editor.isEmpty && placeholder && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              left: 12,
              fontSize: 14,
              color: 'var(--hj-beige-ink-3)',
              pointerEvents: 'none',
            }}
          >
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichText;
